/** How long the hidden download frame stays in the DOM. The browser hands the
 *  response to its download manager as soon as the `Content-Disposition:
 *  attachment` headers arrive, so this only has to outlast the request start
 *  (a presigned S3 GET answers in well under a second). */
export const DOWNLOAD_FRAME_TTL_MS = 60_000;

/**
 * Downloads a presigned URL that carries `Content-Disposition: attachment`
 * through a hidden iframe, so the page itself never navigates.
 *
 * - Not `window.open`: callers resolve the URL after an `await`, by which time
 *   the click's user activation is gone and Safari blocks the popup.
 * - Not `location.assign`: if the object is gone (a deleted batch, an expired
 *   byproduct), S3 answers 403/404 XML and a top-level navigation would
 *   replace the whole SPA with it. Inside the frame that error page is
 *   invisible and the app stays put.
 *
 * Its own module so DOM tests of the callers can mock it.
 */
export function navigateToDownload(url: string): void {
  const frame = document.createElement('iframe');
  frame.style.display = 'none';
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  frame.dataset.downloadFrame = '';
  frame.src = url;
  document.body.appendChild(frame);
  window.setTimeout(() => frame.remove(), DOWNLOAD_FRAME_TTL_MS);
}
